import crypto from 'node:crypto';
import stream from 'node:stream';
import zlib from 'node:zlib';

import type { FsReadCallback } from '../../streams/fsReadTransform.js';
import FsReadTransform from '../../streams/fsReadTransform.js';
import StreamUtil from '../../utils/streamUtil.js';
import File from './file.js';

export const ChecksumBitmask = {
  NONE: 0x0_00,
  CRC32: 0x0_01,
  MD5: 0x0_10,
  SHA1: 0x1_00,
  SHA256: 0x10_00,
} as const;
export type ChecksumBitmaskKey = keyof typeof ChecksumBitmask;
export type ChecksumBitmaskValue = (typeof ChecksumBitmask)[ChecksumBitmaskKey];
export const ChecksumBitmaskInverted = Object.fromEntries(
  Object.entries(ChecksumBitmask).map(([key, value]) => [value, key]),
) as Record<ChecksumBitmaskValue, ChecksumBitmaskKey>;

export interface ChecksumProps {
  crc32?: string;
  md5?: string;
  sha1?: string;
  sha256?: string;
}

export interface ChecksumPropsWithSize extends ChecksumProps {
  size?: number;
}

export default {
  async hashData(data: Buffer | string, checksumBitmask: number): Promise<ChecksumPropsWithSize> {
    if (!checksumBitmask) {
      // Not calculating any checksums, do nothing
      return {};
    }

    const readable = new stream.Readable();
    readable.push(data);
    // eslint-disable-next-line unicorn/no-null
    readable.push(null);
    return await this.hashStream(readable, checksumBitmask);
  },

  async hashFile(
    filePath: string,
    checksumBitmask: number,
    start?: number,
    end?: number,
    callback?: FsReadCallback,
  ): Promise<ChecksumPropsWithSize> {
    if (!checksumBitmask) {
      // Not calculating any checksums, do nothing
      return {};
    }

    return await File.createStreamFromFile(
      filePath,
      async (readable) => await this.hashStream(readable, checksumBitmask, callback),
      start,
      end,
    );
  },

  async hashStream(
    readable: stream.Readable,
    checksumBitmask: number,
    callback?: FsReadCallback,
  ): Promise<ChecksumPropsWithSize> {
    // Not calculating any checksums, do nothing
    if (!checksumBitmask) {
      // WARN(cemmer): this may leave the readable un-drained and therefore some file handles open!
      // We can't call readable.destroy() here because 'unrar' will throw an error
      return {};
    }

    const streamWithCallback =
      callback === undefined
        ? readable
        : StreamUtil.withTransforms(readable, new FsReadTransform(callback));

    let size = 0;
    const hasCrc32 = (checksumBitmask & ChecksumBitmask.CRC32) !== 0;
    let crc = 0;
    const md5 = checksumBitmask & ChecksumBitmask.MD5 ? crypto.createHash('md5') : undefined;
    const sha1 = checksumBitmask & ChecksumBitmask.SHA1 ? crypto.createHash('sha1') : undefined;
    const sha256 =
      checksumBitmask & ChecksumBitmask.SHA256 ? crypto.createHash('sha256') : undefined;

    for await (const chunk of streamWithCallback as AsyncIterable<Buffer>) {
      size += chunk.length;
      if (hasCrc32) {
        crc = zlib.crc32(chunk, crc);
      }
      md5?.update(chunk);
      sha1?.update(chunk);
      sha256?.update(chunk);
    }

    return {
      size,
      crc32: hasCrc32 ? (crc >>> 0).toString(16).padStart(8, '0') : undefined,
      md5: md5?.digest('hex').padStart(32, '0'),
      sha1: sha1?.digest('hex').padStart(40, '0'),
      sha256: sha256?.digest('hex').padStart(64, '0'),
    };
  },

  checksumBitmaskString: (checksumBitmask: number): string =>
    Object.values(ChecksumBitmask)
      .filter((bitmask) => bitmask > 0 && (checksumBitmask & bitmask) > 0)
      .map((bitmask) => ChecksumBitmaskInverted[bitmask])
      .join(', '),
};
